/**
 * FactList (#553) — a definition list of pet facts. Each fact is a
 * `<dt>` micro-label + `<dd>` value; a fact renders ONLY when its value is
 * non-null/non-empty (honesty #546 — never a blank row, never a guessed one).
 *
 * `hasFacts` lets the caller decide between the list and an honest section
 * empty state without duplicating the null-filtering logic.
 */
import type { ReactNode } from 'react';

export interface Fact {
  label: string;
  /** Rendered only when non-null and (for strings) non-blank. */
  value: ReactNode | string | null | undefined;
  /** Optional: force numeric/tabular styling on the value. */
  numeric?: boolean;
}

function isPresent(value: Fact['value']): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

export function factsPresent(facts: ReadonlyArray<Fact>): boolean {
  return facts.some((f) => isPresent(f.value));
}

interface FactListProps {
  facts: ReadonlyArray<Fact>;
  testId?: string;
}

export function FactList({ facts, testId }: FactListProps): JSX.Element | null {
  const present = facts.filter((f) => isPresent(f.value));
  if (present.length === 0) return null;
  return (
    <dl className="ferro-dogprofile__facts" data-testid={testId}>
      {present.map((f) => (
        <div className="ferro-dogprofile__fact" key={f.label}>
          <dt className="ferro-dogprofile__fact-label">{f.label}</dt>
          <dd
            className={
              f.numeric
                ? 'ferro-dogprofile__fact-value ferro-dogprofile__fact-value--num'
                : 'ferro-dogprofile__fact-value'
            }
          >
            {f.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
